import {useTranslation} from '@aboutyou/i18n';
import {
  ImageType,
  ModalDialog,
  tabletAndUp,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {memo, useEffect, useState} from 'react';
import {MiniADPDialog, MiniADPDropdown} from '../MiniADPDialog/MiniADPDialog';
import {useMiniAdpDialogOpenedState} from '../MiniADPDialog/useMiniAdpDialogOpenedState';
/** Article detail blocks are imported from backend-services as this feature simple wraps them together for better reusability. */
import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {TrackingRecoElement} from '@aboutyou/legacy-tracking-sdk';
import {AddToBasketTrackingData} from '@aboutyou/user';
import styled, {css} from 'styled-components';
import {showAddToBasketSuccessToast} from '../../backend-services/ArticleDetail/AddToBasketSection/AddToBasket/hooks/useAutoAddToBasketEffect';
import {AddToBasketSectionMinimal} from '../../backend-services/ArticleDetail/AddToBasketSection/AddToBasketSectionMinimal';
import {SoldOutSizeReminderFlyout} from '../../backend-services/ArticleDetail/AddToBasketSection/SoldOutSizeReminderFlyout';
import {PriceAndBrandMinimal} from '../../backend-services/ArticleDetail/PriceAndBrand/PriceAndBrandMinimal';
import {ProductSelectionMinimal} from '../../backend-services/ArticleDetail/ProductSelection/ProductSelectionMinimal';
import {ProductTileImageSlider} from '../../backend-services/ArticleDetail/ProductTileImageSlider/ProductTileImageSlider';
import {ProminentBannerSectionMinimal} from '../../backend-services/ArticleDetail/ProminentBannerSection/ProminentBannerSectionMinimal';
import {MerchantInfoWithLegal} from '../../backend-services/MerchantInfo/MerchantInfoWithLegal';

import {AddToBasketOnSuccessProps} from '../SizeSelectionFlyout/hooks/useAddToBasket';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';

const MemoPriceAndBrandMinimal = memo(PriceAndBrandMinimal);
const MemoProminentBannerSection = memo(ProminentBannerSectionMinimal);
const MemoProductSelectionMinimal = memo(ProductSelectionMinimal);
const MemoAddToBasketSectionMinimal = memo(AddToBasketSectionMinimal);
const MemoProductTileImageSlider = memo(ProductTileImageSlider);
const MemoSoldOutSizeReminderFlyout = memo(SoldOutSizeReminderFlyout);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ProductMiniAdpProps
  extends Omit<ReturnType<typeof useMiniAdpDialogOpenedState>, 'open'> {
  onSuccess?: (
    props: {
      productId?: number;
    } & AddToBasketOnSuccessProps,
  ) => void;
  showToastMessage?: boolean;
  sizeSelectionStateManagerKey: SizeSelectionOpenedStateKey;
  showAddToWishlistButton?: boolean;
  trackingData?: {
    sectionIndex?: number;
    sortingChannel?: string;
    pageType?: string;
    recoType?: string;
    elements?: TrackingRecoElement[];
  };
  renderInPortal?: boolean;
  overrideBETrackingDataState?: AddToBasketTrackingData;
  couponPromotionId?: string;
}

export const ProductMiniAdp: React.FC<ProductMiniAdpProps> = ({
  close,
  isOpen,
  productId,
  filteredSizeId,
  selectedSize,
  setSelectedSize,
  onSuccess,
  sizeSelectionStateManagerKey,
  showToastMessage = false,
  showAddToWishlistButton = false,
  trackingData,
  renderInPortal,
  overrideBETrackingDataState,
  couponPromotionId,
}) => {
  const {t} = useTranslation();
  const [lastProductId, setLastProductId] = useState(productId);

  const [showSoldOutSizeReminder, setShowSoldOutSizeReminder] = useState(false);
  const {isOpen: isSizeSelectionOpen, cancel: cancelSizeSelection} =
    useSizeSelectionOpenedState(sizeSelectionStateManagerKey);
  const [previewImage, setPreviewImage] = useState<ImageType>();

  const showToast = useShowToast();

  /**
   * TODO: this should be internal to the `useMiniAdpDialogOpenedState` hook to manage the modal props. https://aboutyou.atlassian.net/browse/AYFEP-2394
   * For now this is a good enough solution, though the above should be addressed.
   * Only applies at the wishlist.
   */
  useEffect(() => {
    if (productId) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setLastProductId(productId);
    }
  }, [productId]);

  const onAddToBasketSuccessHandler = ({
    freeDeliverySection,
    statusSuccessSections,
  }: {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    close();
    if (showToastMessage) {
      showAddToBasketSuccessToast(showToast, t);
    }
    onSuccess?.({
      productId: lastProductId,
      freeDeliverySection,
      statusSuccessSections,
    });
  };

  const onCloseHandler = () => {
    if (isSizeSelectionOpen) {
      /**
       * If size selection stays open, the modal-outside click won't close the modal,
       * but the size selection only.
       */

      cancelSizeSelection();
    }
    close();
  };

  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setPreviewImage(undefined);
    }
  }, [isOpen]);

  const activeProductId = lastProductId || productId!;

  return (
    <MiniADPDialog
      isOpen={isOpen}
      onClose={onCloseHandler}
      renderInPortal={renderInPortal}
      imageSlider={
        <MemoProductTileImageSlider
          redirectOnClick
          showThumbnails
          showAddToWishlistButton={showAddToWishlistButton}
          productId={activeProductId}
          replaceHistory
          skipDefaultState
          previewImage={previewImage}
          isInMiniADPDialog={true}
        />
      }
      priceAndBrand={
        <MemoPriceAndBrandMinimal
          brandLogoAlignment="left"
          selectedSize={selectedSize}
          productId={activeProductId}
          skipDefaultState
          PriceLegalInfoLayoutComponent={ModalDialog}
        />
      }
      prominentBanner={
        <MemoProminentBannerSection
          productId={activeProductId}
          selectedCouponPromotionId={couponPromotionId}
          couponVariant={'coupon-sticky-bar'}
          hideCampaignCountdown
        />
      }
      productSelection={
        <>
          <MemoProductSelectionMinimal
            disablePreviewImage
            disableRedirect
            ColorSelectionLayoutComponent={MiniADPDropdown}
            productId={activeProductId}
            onProductSelectionClick={productId => {
              setLastProductId(productId);
              setSelectedSize(undefined);
            }}
            setPreviewImage={setPreviewImage}
            isInMiniADPDialog
          />
          <MemoSoldOutSizeReminderFlyout
            setIsOpenSizeReminder={setShowSoldOutSizeReminder}
            sizeSelectionStateManagerKey={
              SizeSelectionOpenedStateKey.NO_SIZE_SELECTED_SELECTION
            }
            isOpenSizeReminder={showSoldOutSizeReminder}
          />
        </>
      }
      sizeSelection={
        <>
          <MemoAddToBasketSectionMinimal
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            showPriceInSizeSelectionHeader={false}
            showSizeAdvisor={true}
            SizeReminderLayoutComponent={ModalDialog}
            SizeSelectionLayoutComponent={MiniADPDropdown}
            showWishlistButton={false}
            productId={activeProductId}
            onAddToBasketSuccess={onAddToBasketSuccessHandler}
            filteredSizeId={filteredSizeId}
            replaceHistory
            trackingData={trackingData}
            sizeSelectionStateManagerKey={sizeSelectionStateManagerKey}
            overrideBETrackingDataState={overrideBETrackingDataState}
          />
          {selectedSize && productId && (
            <StyledMerchantInfo
              availability={selectedSize.availability}
              productId={productId}
              sizeId={selectedSize.sizeId}
              showBorderRadius
            />
          )}
        </>
      }
    />
  );
};

const StyledMerchantInfo = styled(MerchantInfoWithLegal)`
  margin-top: 10px;

  ${tabletAndUp(css`
    max-width: 270px;
    width: 100%;
  `)}
`;
